import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { X, ChevronRight, ChevronLeft, LayoutDashboard, Brain, Users, CalendarDays, ClipboardCheck } from 'lucide-react';

const TOUR_STEPS = [
  {
    title: 'Benvenuto in HumanAImpact',
    description: 'Questa piattaforma integra agenti AI per gestire marketing, comunicazione e vendite. Ecco una panoramica delle 5 aree principali.',
    icon: LayoutDashboard,
    highlight: null,
  },
  {
    title: 'Dashboard',
    description: 'La tua base operativa: KPI in tempo reale, le tue priorita di oggi, azioni rapide e una panoramica completa del business. Inizia sempre da qui.',
    icon: LayoutDashboard,
    highlight: '/command',
  },
  {
    title: 'Marketing',
    description: 'Contenuti, Approvazione, Campagne ed Eventi: tutto in un unico menu. Crea, revisiona e pubblica con l\'aiuto degli agenti AI.',
    icon: Brain,
    highlight: '/contents',
  },
  {
    title: 'Vendite',
    description: 'CRM Contatti con tabella arricchita, Pipeline & Funnel unificati e Catalogo Prodotti. Il cuore commerciale della piattaforma.',
    icon: Users,
    highlight: '/contacts',
  },
  {
    title: 'Partnership & Sistema',
    description: 'Gestisci il network professionale nella sezione Partnership. In Sistema trovi Dati Azienda, Team Agenti e Tester UX.',
    icon: CalendarDays,
    highlight: '/network',
  },
  {
    title: 'Pronto per iniziare!',
    description: 'Vai alla Dashboard per vedere le tue priorita di oggi. Usa la Ricerca Globale nell\'header per trovare qualsiasi cosa.',
    icon: ClipboardCheck,
    highlight: '/command',
  },
];

export function OnboardingTour({ onComplete }) {
  const [step, setStep] = useState(0);
  const [visible, setVisible] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const done = localStorage.getItem('hai_tour_done');
    if (done) { setVisible(false); onComplete?.(); }
  }, [onComplete]);

  if (!visible) return null;

  const current = TOUR_STEPS[step];
  const Icon = current.icon;
  const isLast = step === TOUR_STEPS.length - 1;

  const finish = () => {
    localStorage.setItem('hai_tour_done', 'true');
    setVisible(false);
    onComplete?.();
    navigate('/command');
  };

  const next = () => {
    if (isLast) { finish(); return; }
    const nextStep = TOUR_STEPS[step + 1];
    if (nextStep.highlight) navigate(nextStep.highlight);
    setStep(step + 1);
  };

  const prev = () => {
    if (step === 0) return;
    const prevStep = TOUR_STEPS[step - 1];
    if (prevStep.highlight) navigate(prevStep.highlight);
    setStep(step - 1);
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center" data-testid="onboarding-tour">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div className="relative bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 p-6 animate-in fade-in zoom-in-95 duration-300">
        <button onClick={finish} className="absolute top-3 right-3 text-[#94A3B8] hover:text-[#1A3A5C]" data-testid="tour-skip">
          <X className="w-4 h-4" />
        </button>

        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-[#1A3A5C] flex items-center justify-center">
            <Icon className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="text-xs text-[#94A3B8]">Passo {step + 1} di {TOUR_STEPS.length}</p>
            <h3 className="text-base font-bold text-[#1A3A5C]">{current.title}</h3>
          </div>
        </div>

        <p className="text-sm text-[#334155] leading-relaxed mb-6">{current.description}</p>

        {/* Progress dots */}
        <div className="flex items-center justify-center gap-1.5 mb-4">
          {TOUR_STEPS.map((_, i) => (
            <div key={i} className={`w-2 h-2 rounded-full transition-colors ${i === step ? 'bg-[#2E75B6]' : i < step ? 'bg-[#2E75B6]/40' : 'bg-[#E2E8F0]'}`} />
          ))}
        </div>

        <div className="flex items-center justify-between">
          <Button size="sm" variant="ghost" onClick={prev} disabled={step === 0} className="text-xs" data-testid="tour-prev">
            <ChevronLeft className="w-3.5 h-3.5 mr-1" />Indietro
          </Button>
          <Button size="sm" variant="ghost" onClick={finish} className="text-xs text-[#64748B]" data-testid="tour-finish">
            Salta tour
          </Button>
          <Button size="sm" className="bg-[#1A3A5C] hover:bg-[#2E75B6] text-xs" onClick={next} data-testid="tour-next">
            {isLast ? 'Inizia!' : 'Avanti'}<ChevronRight className="w-3.5 h-3.5 ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
}
